import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";

type AppRole = "owner" | "admin" | "editor" | "viewer" | null;

interface RoleContextType {
  role: AppRole;
  loading: boolean;
  isOwnerOrAdmin: boolean;
  canManageContent: boolean;
  error: string | null;
  retry: () => void;
}

const RoleContext = createContext<RoleContextType>({
  role: null,
  loading: true,
  isOwnerOrAdmin: false,
  canManageContent: false,
  error: null,
  retry: () => {},
});

export function useUserRole() {
  return useContext(RoleContext);
}

export function RoleProvider({ children }: { children: ReactNode }) {
  const [role, setRole] = useState<AppRole>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchRoleForSession = async (session: { user?: { id: string } } | null) => {
    if (!session?.user) {
      setRole(null);
      setError(null);
      setLoading(false);
      return;
    }

    try {
      const { data, error: rpcError } = await supabase.rpc("get_user_role", { _user_id: session.user.id });
      if (rpcError) {
        setError(rpcError.message);
        setRole(null);
      } else {
        setError(null);
        setRole((data as AppRole) || null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch role");
      setRole(null);
    } finally {
      setLoading(false);
    }
  };

  const retry = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) {
        setError(sessionError.message);
        setLoading(false);
        return;
      }
      await fetchRoleForSession(session);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to refresh session");
      setLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!mounted) return;

      if (event === "SIGNED_OUT") {
        setRole(null);
        setError(null);
        setLoading(false);
        return;
      }

      // Update last_login_at on sign-in events
      if ((event === "SIGNED_IN" || event === "TOKEN_REFRESHED") && session?.user) {
        supabase
          .from("profiles")
          .update({ last_login_at: new Date().toISOString() })
          .eq("id", session.user.id)
          .then(() => {});
      }

      setLoading(true);
      void fetchRoleForSession(session);
    });

    void supabase.auth.getSession()
      .then(({ data: { session }, error: sessionError }) => {
        if (!mounted) return;
        if (sessionError) {
          setError(sessionError.message);
          setLoading(false);
          return;
        }
        void fetchRoleForSession(session);
      })
      .catch((err) => {
        if (!mounted) return;
        setError(err instanceof Error ? err.message : "Failed to load session");
        setLoading(false);
      });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const isOwnerOrAdmin = role === "owner" || role === "admin";
  const canManageContent = isOwnerOrAdmin || role === "editor";

  return (
    <RoleContext.Provider value={{ role, loading, isOwnerOrAdmin, canManageContent, error, retry }}>
      {children}
    </RoleContext.Provider>
  );
}

